import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { FaFilter } from 'react-icons/fa';
import API_BASE_URL from '../../../components/apiconfig/api-config';

const formatDate = (dateString) => {
  if (!dateString || dateString === 'null' || dateString === 'undefined' || dateString === '') {
    return '-';
  }
  const date = new Date(dateString);
  // Check if date is valid (not 1970-01-01 or invalid)
  if (isNaN(date.getTime()) || date.getFullYear() === 1970) {
    return '-';
  }
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = String(date.getFullYear()).slice(-2);
  return `${day}/${month}/${year}`;
};

const ROWS_PER_PAGE = 15;

const SalesReportsWarehouse = () => {
  const [data, setData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  
  // Updated filter state with sales contract date filters
  const [filters, setFilters] = useState({
    purchaseContractNo: '',
    salesContractNo: '',
    warehouse: '',
    cmaName: '',
    status: '',
    commodity: '',
    salesContractDateFrom: '',
    salesContractDateTo: ''
  });
  
  const [filterOptions, setFilterOptions] = useState({
    purchaseContractNos: [],
    salesContractNos: [],
    warehouses: [],
    cmaNames: [],
    statuses: [],
    commodities: []
  });
  const [showFilters, setShowFilters] = useState(false);
  const [page, setPage] = useState(1);

  useEffect(() => {
    axios
      .get(`${API_BASE_URL}/api/report/sales-reports-warehouse`, { withCredentials: true })
      .then(res => {
        setData(res.data);
        setFilteredData(res.data);
        
        // Extract unique filter options
        const purchaseContractNos = [...new Set(res.data.map(item => item.purchase_contract_no))];
        const salesContractNos = [...new Set(res.data.map(item => item.sales_contract_no))];
        const warehouses = [...new Set(res.data.map(item => item.warehouse))];
        const cmaNames = [...new Set(res.data.map(item => item.cma_name))];
        const statuses = [...new Set(res.data.map(item => item.status))];
        const commodities = [...new Set(res.data.map(item => item.commodity))];
        
        setFilterOptions({
          purchaseContractNos: purchaseContractNos,
          salesContractNos: salesContractNos,
          warehouses: warehouses,
          cmaNames: cmaNames,
          statuses: statuses,
          commodities: commodities
        });
      })
      .catch(err => console.error(err));
  }, []);

  useEffect(() => {
    setPage(1); // Reset to first page when filters change
  }, [filters]);

  // Updated filtering logic with Sales Contract Date range filtering
  useEffect(() => {
    const filtered = data.filter(item => {
      // Existing filters
      const matchesPurchaseContract = filters.purchaseContractNo === '' || item.purchase_contract_no === filters.purchaseContractNo;
      const matchesSalesContract = filters.salesContractNo === '' || item.sales_contract_no === filters.salesContractNo;
      const matchesWarehouse = filters.warehouse === '' || item.warehouse === filters.warehouse;
      const matchesCmaName = filters.cmaName === '' || item.cma_name === filters.cmaName;
      const matchesStatus = filters.status === '' || item.status === filters.status;
      const matchesCommodity = filters.commodity === '' || item.commodity === filters.commodity;

      // Sales Contract Date range filtering
      let matchesDateRange = true;
      if (filters.salesContractDateFrom || filters.salesContractDateTo) {
        const recordDate = new Date(item.contract_date);
        
        if (filters.salesContractDateFrom && filters.salesContractDateTo) {
          // Both from and to dates provided - filter within range
          const fromDate = new Date(filters.salesContractDateFrom);
          const toDate = new Date(filters.salesContractDateTo);
          matchesDateRange = recordDate >= fromDate && recordDate <= toDate;
        } else if (filters.salesContractDateTo) {
          // Only to date provided - filter from beginning to that date
          const toDate = new Date(filters.salesContractDateTo);
          matchesDateRange = recordDate <= toDate;
        } else if (filters.salesContractDateFrom) {
          // Only from date provided - filter from that date onwards
          const fromDate = new Date(filters.salesContractDateFrom);
          matchesDateRange = recordDate >= fromDate;
        }
      }

      return matchesPurchaseContract && matchesSalesContract && matchesWarehouse && 
             matchesCmaName && matchesStatus && matchesCommodity && matchesDateRange;
    });
    setFilteredData(filtered);
  }, [filters, data]);

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Updated reset function to include date filters
  const resetFilters = () => {
    setFilters({
      purchaseContractNo: '',
      salesContractNo: '',
      warehouse: '',
      cmaName: '',
      status: '',
      commodity: '',
      salesContractDateFrom: '',
      salesContractDateTo: ''
    });
  };

  const toggleFilters = () => {
    setShowFilters(!showFilters);
  };

  // Function to calculate grand total
  const calculateGrandTotal = (data) => {
    return data.reduce((total, record) => ({
      contract_qty: total.contract_qty + (parseFloat(record.contract_quantity_mt) || 0),
      cancelled_qty: total.cancelled_qty + (parseFloat(record.cancelled_qty) || 0),
      ordered_qty: total.ordered_qty + (parseFloat(record.ordered_quantity_mt) || 0),
      contract_value: total.contract_value + ((parseFloat(record.contract_quantity_mt) || 0) * (parseFloat(record.price_per_mt) || 0)),
      count: total.count + 1
    }), { contract_qty: 0, cancelled_qty: 0, ordered_qty: 0, contract_value: 0, count: 0 });
  };

  const grandTotal = calculateGrandTotal(filteredData);

 const formatNumber = (number, decimals = 3) => {
  if (number === null || number === undefined || number === '') return '-';
  const num = parseFloat(number).toFixed(decimals);
  return parseFloat(num).toLocaleString('en-IN', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals
  });
};


  // Function to truncate text to first two words
  const truncateToTwoWords = (text) => {
    if (!text) return '-';
    const words = text.split(' ');
    return words.slice(0, 4).join(' ') + (words.length > 4 ? '...' : '');
  };

  // Function to truncate CMA name
  const truncateCMA = (text) => {
    if (!text) return '-';
    const maxLength = 20;
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + '...';
  };

  const totalPages = Math.ceil(filteredData.length / ROWS_PER_PAGE);
  const handlePrevPage = () => setPage((p) => Math.max(1, p - 1));
  const handleNextPage = () => setPage((p) => Math.min(totalPages, p + 1));
  const paginatedData = filteredData.slice((page - 1) * ROWS_PER_PAGE, page * ROWS_PER_PAGE);

  return (
    <div>
      <div className="bg-white shadow-md p-3 sm:p-6">
        <div className="flex flex-col sm:flex-row justify-between items-center mb-6 space-y-3 sm:space-y-0">
          <h2 className="text-lg sm:text-xl font-semibold text-gray-700 text-center sm:text-left">
            Sales Contract Status   
          </h2>
          <button
            onClick={toggleFilters}
            className="flex items-center gap-2 px-3 sm:px-4 py-2 bg-blue-100 text-[#3899cf] rounded-md hover:bg-blue-200 transition text-sm w-full sm:w-auto justify-center"
          >
            <FaFilter />
            <span className="hidden sm:inline">{showFilters ? 'Hide Filters' : 'Show Filters'}</span>
            <span className="sm:hidden">{showFilters ? 'Hide' : 'Filters'}</span>
          </button>
        </div>

        {/* Enhanced Filter Section with Sales Contract Date Range */}
        {showFilters && (
          <div className="mb-6 p-3 sm:p-4 bg-gray-50 rounded-lg">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 sm:gap-4">
              {/* Purchase Contract No */}
              <div className="flex flex-col">
                <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">Purchase Contract No</label>
                <select
                  name="purchaseContractNo"
                  value={filters.purchaseContractNo}
                  onChange={handleFilterChange}
                  className="p-2 border border-gray-300 rounded-md focus:ring-[#3899cf] focus:border-[#3899cf] text-sm"
                >
                  <option value="">All Contracts</option>
                  {filterOptions.purchaseContractNos.map((contract, index) => (
                    <option key={index} value={contract}>{contract}</option>
                  ))}
                </select>
              </div>

              {/* Sales Contract No */}
              <div className="flex flex-col">
                <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">Sales Contract No</label>
                <select
                  name="salesContractNo"
                  value={filters.salesContractNo}
                  onChange={handleFilterChange}
                  className="p-2 border border-gray-300 rounded-md focus:ring-[#3899cf] focus:border-[#3899cf] text-sm"
                >
                  <option value="">All Contracts</option>
                  {filterOptions.salesContractNos.map((contract, index) => (
                    <option key={index} value={contract}>{contract}</option>
                  ))}
                </select>
              </div>

              {/* Sales Contract Date From Filter */}
              <div className="flex flex-col">
                <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">Sales Contract Date From</label>
                <input
                  type="date"
                  name="salesContractDateFrom"
                  value={filters.salesContractDateFrom}
                  onChange={handleFilterChange}
                  className="p-2 border border-gray-300 rounded-md focus:ring-[#3899cf] focus:border-[#3899cf] text-sm"
                />
              </div>

              {/* Sales Contract Date To Filter */}
              <div className="flex flex-col">
                <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">Sales Contract Date To</label>
                <input
                  type="date"
                  name="salesContractDateTo"
                  value={filters.salesContractDateTo}
                  onChange={handleFilterChange}
                  className="p-2 border border-gray-300 rounded-md focus:ring-[#3899cf] focus:border-[#3899cf] text-sm"
                />
              </div>

              {/* Commodity Filter */}
              <div className="flex flex-col">
                <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">Commodity</label>
                <select
                  name="commodity"
                  value={filters.commodity}
                  onChange={handleFilterChange}
                  className="p-2 border border-gray-300 rounded-md focus:ring-[#3899cf] focus:border-[#3899cf] text-sm"
                >
                  <option value="">All Commodities</option>
                  {filterOptions.commodities.map((commodity, index) => (
                    <option key={index} value={commodity}>{commodity}</option>
                  ))}
                </select>
              </div>

              {/* Warehouse */}
              <div className="flex flex-col">
                <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">Warehouse</label>
                <select
                  name="warehouse"
                  value={filters.warehouse}
                  onChange={handleFilterChange}
                  className="p-2 border border-gray-300 rounded-md focus:ring-[#3899cf] focus:border-[#3899cf] text-sm"
                >
                  <option value="">All Warehouses</option>
                  {filterOptions.warehouses.map((warehouse, index) => (
                    <option key={index} value={warehouse}>{warehouse}</option>
                  ))}
                </select>
              </div>

              {/* CMA Name */}
              <div className="flex flex-col">
                <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">CMA</label>
                <select
                  name="cmaName"
                  value={filters.cmaName}
                  onChange={handleFilterChange}
                  className="p-2 border border-gray-300 rounded-md focus:ring-[#3899cf] focus:border-[#3899cf] text-sm"
                >
                  <option value="">All CMAs</option>
                  {filterOptions.cmaNames.map((cma, index) => (
                    <option key={index} value={cma}>{cma}</option>
                  ))}
                </select>
              </div>

              {/* Status */}
              <div className="flex flex-col">
                <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">Status</label>
                <select
                  name="status"
                  value={filters.status}
                  onChange={handleFilterChange}
                  className="p-2 border border-gray-300 rounded-md focus:ring-[#3899cf] focus:border-[#3899cf] text-sm"
                >
                  <option value="">All Statuses</option>
                  {filterOptions.statuses.map((status, index) => (
                    <option key={index} value={status}>{status}</option>
                  ))}
                </select>
              </div>

              {/* Reset Button */}
              <div className="flex items-end col-span-full">
                <button
                  onClick={resetFilters}
                  className="px-4 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 transition text-sm w-full sm:w-auto"
                >
                  Reset Filters
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Table */}
        <div className="overflow-x-auto overflow-y-auto max-h-96" style={{ scrollbarWidth: 'thin', scrollbarColor: '#cbd5e1 #f1f5f9' }}>
          <style jsx>{`
            div::-webkit-scrollbar {
              width: 6px;
              height: 6px;
            }
            div::-webkit-scrollbar-track {
              background: #f1f5f9;
            }
            div::-webkit-scrollbar-thumb {
              background: #cbd5e1;
              border-radius: 3px;
            }
            div::-webkit-scrollbar-thumb:hover {
              background: #94a3b8;
            }
          `}</style>
          <table className="min-w-max w-full text-xs sm:text-sm border-collapse border border-gray-300">
            <thead className="bg-gray-100 sticky top-0">
              <tr className="text-left text-gray-600">
                <th className="px-2 sm:px-3 py-2 border border-gray-300 min-w-[90px]">
                  <span className="hidden sm:inline">Purchase Contract No</span>
                  <span className="sm:hidden">Contract</span>
                </th>
                <th className="px-2 sm:px-3 py-2 border border-gray-300 min-w-[90px] hidden md:table-cell">Sales Contract No</th>
                <th className="px-2 sm:px-3 py-2 border border-gray-300 min-w-[85px] hidden lg:table-cell">SC Date</th>
                <th className="px-2 sm:px-3 py-2 border border-gray-300 min-w-[90px] hidden lg:table-cell">Commodity</th>
                <th className="px-2 sm:px-3 py-2 border border-gray-300 min-w-[70px] hidden xl:table-cell">Price (MT)</th>
                <th className="px-2 sm:px-3 py-2 border border-gray-300 min-w-[75px] hidden xl:table-cell">Start Date</th>
                <th className="px-2 sm:px-3 py-2 border border-gray-300 min-w-[75px] hidden xl:table-cell">End Date</th>
                <th className="px-2 sm:px-3 py-2 border border-gray-300 min-w-[85px] hidden md:table-cell">Warehouse</th>
                <th className="px-2 sm:px-3 py-2 border border-gray-300 min-w-[90px] hidden lg:table-cell">CMA</th>
                <th className="px-2 sm:px-3 py-2 border border-gray-300 min-w-[80px]">
                  <span className="hidden sm:inline">SC Qty (MT)</span>
                  <span className="sm:hidden">Qty</span>
                </th>
                <th className="px-2 sm:px-3 py-2 border border-gray-300 min-w-[90px]">
                  <span className="hidden sm:inline">Cancelled Qty. (MT)</span>
                  <span className="sm:hidden">Cancel</span>
                </th>
                <th className="px-2 sm:px-3 py-2 border border-gray-300 min-w-[70px] hidden md:table-cell">DO Qty (MT)</th>
                <th className="px-2 sm:px-3 py-2 border border-gray-300 min-w-[90px] hidden lg:table-cell">Last SO Date</th>
                <th className="px-2 sm:px-3 py-2 border border-gray-300 min-w-[70px] hidden xl:table-cell">Days Expired</th>
                <th className="px-2 sm:px-3 py-2 border border-gray-300 min-w-[90px] hidden md:table-cell">SC Qty Value </th>
                <th className="px-2 sm:px-3 py-2 border border-gray-300 min-w-[70px]">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {paginatedData.length > 0 ? (
                paginatedData.map((row, index) => (
                  <tr key={index + (page - 1) * ROWS_PER_PAGE} className="hover:bg-blue-50">
                    <td className="px-2 sm:px-3 py-2 border border-gray-200 font-medium text-xs sm:text-sm">
                      <div>{row.purchase_contract_no}</div>
                      <div className="text-xs text-gray-500 sm:hidden">{row.sales_contract_no}</div>
                    </td>
                    <td className="px-2 sm:px-3 py-2 border border-gray-200 text-xs sm:text-sm hidden md:table-cell">{row.sales_contract_no}</td>
                    <td className="px-2 sm:px-3 py-2 border border-gray-200 text-xs sm:text-sm hidden lg:table-cell">{formatDate(row.contract_date)}</td>
                    <td className="px-2 sm:px-3 py-2 border border-gray-200 cursor-pointer text-xs sm:text-sm hidden lg:table-cell" title={row.commodity || '-'}>
                      {truncateToTwoWords(row.commodity)}
                    </td>
                    <td className="px-2 sm:px-3 py-2 border border-gray-200 text-xs sm:text-sm hidden xl:table-cell">{Number(row.price_per_mt).toFixed(2)}</td>
                    <td className="px-2 sm:px-3 py-2 border border-gray-200 text-xs sm:text-sm hidden xl:table-cell">{formatDate(row.delivery_start_date)}</td>
                    <td className="px-2 sm:px-3 py-2 border border-gray-200 text-xs sm:text-sm hidden xl:table-cell">{formatDate(row.delivery_end_date)}</td>
                    <td className="px-2 sm:px-3 py-2 border border-gray-200 text-xs sm:text-sm hidden md:table-cell">{row.warehouse}</td>
                    <td className="px-2 sm:px-3 py-2 border border-gray-200 cursor-pointer text-xs sm:text-sm hidden lg:table-cell" title={row.cma_name || '-'}>
                      {truncateCMA(row.cma_name)}
                    </td>
                    <td className="px-2 sm:px-3 py-2 border border-gray-200 text-right text-xs sm:text-sm">
                      <div className="font-medium">{Number(row.contract_quantity_mt).toFixed(3)}</div>
                      <div className="text-xs text-gray-500 sm:hidden">{row.warehouse}</div>
                    </td>
                    <td className="px-2 sm:px-3 py-2 border border-gray-200 text-right text-xs sm:text-sm font-medium text-red-600">
                      {Number(row.cancelled_qty || 0).toFixed(3)}
                    </td>
                    <td className="px-2 sm:px-3 py-2 border border-gray-200 text-right text-xs sm:text-sm hidden md:table-cell">{Number(row.ordered_quantity_mt).toFixed(3)}</td>
                    <td className="px-2 sm:px-3 py-2 border border-gray-200 text-xs sm:text-sm hidden lg:table-cell">{row.last_sales_order_date ? formatDate(row.last_sales_order_date) : '-'}</td>
                    <td className="px-2 sm:px-3 py-2 border border-gray-200 text-xs sm:text-sm hidden xl:table-cell">{row.days_contract_expired}</td>
                    <td className="px-2 sm:px-3 py-2 border border-gray-200 text-xs sm:text-sm hidden md:table-cell">
  <div className="flex justify-between">
    <span>₹</span>
    <span className="text-right">
      {formatNumber(
        (parseFloat(row.contract_quantity_mt) || 0) *
        (parseFloat(row.price_per_mt) || 0),
        2
      )}
    </span>
  </div>
</td>

                    <td className="px-2 sm:px-3 py-2 border border-gray-200 font-semibold text-xs sm:text-sm">{row.status}</td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="15" className="text-center p-8 text-gray-500 italic text-xs sm:text-sm">
                    {data.length === 0 ? 'No sales contract records found.' : 'No records match the selected filters.'}
                  </td>
                </tr>
              )}
              
              {/* Grand Total Row - Only show on last page or when single page */}
              {/* Grand Total Row - Only show on last page or when single page */}
{(page === totalPages || totalPages === 0) && filteredData.length > 0 && (
  <tr className="bg-blue-100 font-bold text-blue-900">
    <td className="px-2 sm:px-3 py-3 border border-gray-300" colSpan="4">
      <span className="hidden sm:inline">GRAND TOTAL ({grandTotal.count} contracts)</span>
      <span className="sm:hidden">TOTAL ({grandTotal.count})</span>
    </td>
    {/* Average Price Per MT Column */}
    <td className="px-2 sm:px-3 py-3 border border-gray-300 text-right font-bold text-xs sm:text-sm hidden xl:table-cell">
      {formatNumber(
        grandTotal.contract_qty > 0 
          ? grandTotal.contract_value / grandTotal.contract_qty 
          : 0, 
        2
      )}
    </td>
    <td className="px-2 sm:px-3 py-3 border border-gray-300 text-right text-xs sm:text-sm hidden xl:table-cell" colSpan="4">-</td>
    <td className="px-2 sm:px-3 py-3 border border-gray-300 text-right font-bold text-xs sm:text-sm">
      {formatNumber(grandTotal.contract_qty, 3)}
    </td>
    <td className="px-2 sm:px-3 py-3 border border-gray-300 text-right font-bold text-xs sm:text-sm text-red-700">
      {formatNumber(grandTotal.cancelled_qty, 3)}
    </td>
    <td className="px-2 sm:px-3 py-3 border border-gray-300 text-right font-bold text-xs sm:text-sm hidden md:table-cell">
      {formatNumber(grandTotal.ordered_qty, 3)}
    </td>
    <td className="px-2 sm:px-3 py-3 border border-gray-300 text-right text-xs sm:text-sm hidden lg:table-cell">-</td>
    <td className="px-2 sm:px-3 py-3 border border-gray-300 text-right text-xs sm:text-sm hidden xl:table-cell">-</td>
    <td className="px-2 sm:px-3 py-3 border border-gray-300 text-right font-bold text-xs sm:text-sm hidden md:table-cell">
      <div className="flex justify-between">
        <span>₹</span>
        <span className="text-right">
          {formatNumber(grandTotal.contract_value, 2)}
        </span>
      </div>
    </td>
    <td className="px-2 sm:px-3 py-3 border border-gray-300 text-right text-xs sm:text-sm">-</td>
  </tr>
)}

            </tbody>
          </table>
        </div>
        
        {/* Pagination Controls */}
        {filteredData.length > ROWS_PER_PAGE && (
          <div className="flex flex-col sm:flex-row justify-center sm:justify-end items-center gap-3 sm:gap-2 mt-3 text-xs">
            <div className="flex items-center gap-2">
              <button
                onClick={handlePrevPage}
                disabled={page === 1}
                className={`px-3 sm:px-2 py-2 sm:py-1 rounded border transition-colors duration-100 ${page === 1 ? 'bg-gray-100 text-gray-300 border-gray-200' : 'bg-white text-blue-500 border-blue-500 hover:text-white hover:bg-blue-500'}`}
              >
                <span className="hidden sm:inline">Prev</span>
                <span className="sm:hidden">Previous</span>
              </button>
              <span className="text-gray-600 px-2">
                Page {page} of {totalPages}
              </span>
              <button
                onClick={handleNextPage}
                disabled={page === totalPages}
                className={`px-3 sm:px-2 py-2 sm:py-1 rounded border transition-colors duration-100 ${page === totalPages ? 'bg-gray-100 text-gray-300 border-gray-200' : 'bg-white text-blue-500 border-blue-500 hover:text-white hover:bg-blue-500'}`}
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SalesReportsWarehouse;
